import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  ColDef,
  RowDoubleClickedEvent,
  SelectionChangedEvent,
} from 'ag-grid-community';
import { X } from 'lucide-react';
import { LopAgGrid } from '../LopAgGrid';
import { searchRecords } from '../../api/search';
import { t } from '../../i18n/t';
import './LopSearchModal.css';

type Row = Record<string, unknown>;

export interface LopSearchModalProps {
  title: string;
  table: string; // for searchRecords
  columns: { field: string; headerName: string }[]; // grid display columns
  onSelect: (row: Row) => void;
  onClose: () => void;
}

const DEBOUNCE_MS = 250;

// Generic modal record-picker. Rides on the parent screen's permissions — no
// permission logic here. Reusable infra.
export function LopSearchModal({
  title,
  table,
  columns,
  onSelect,
  onClose,
}: LopSearchModalProps) {
  const [term, setTerm] = useState('');
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [selected, setSelected] = useState<Row | null>(null);

  const columnDefs = useMemo<ColDef<Row>[]>(
    () =>
      columns.map((c) => ({ field: c.field, headerName: c.headerName })),
    [columns],
  );

  // Debounced search. An empty term browses the top N records (initial browse
  // on mount + whenever the user clears the box).
  useEffect(() => {
    let cancelled = false;
    const handle = window.setTimeout(() => {
      setLoading(true);
      searchRecords(table, term)
        .then((data) => {
          if (cancelled) return;
          setRows(data);
          setSearched(true);
        })
        .catch(() => {
          if (cancelled) return;
          setRows([]);
          setSearched(true);
        })
        .finally(() => {
          if (cancelled) return;
          setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [table, term]);

  // Esc closes the dialog.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const handleConfirm = useCallback(
    (row: Row | null) => {
      if (!row) return;
      onSelect(row);
      onClose();
    },
    [onSelect, onClose],
  );

  const onSelectionChanged = useCallback(
    (e: SelectionChangedEvent<Row>) => {
      const selectedRows = e.api.getSelectedRows();
      setSelected(selectedRows[0] ?? null);
    },
    [],
  );

  const onRowDoubleClicked = useCallback(
    (e: RowDoubleClickedEvent<Row>) => {
      if (e.data) handleConfirm(e.data);
    },
    [handleConfirm],
  );

  const showEmpty = !loading && searched && rows.length === 0 && term.trim() !== '';

  return (
    <div
      className="lop-search-modal__backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="lop-search-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="lop-search-modal__header">
          <h2 className="lop-search-modal__title">{title}</h2>
          <button
            type="button"
            className="lop-search-modal__close"
            aria-label={t('recordForm.close')}
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </header>

        <div className="lop-search-modal__searchbar">
          <input
            type="text"
            className="lop-search-modal__search-input"
            value={term}
            placeholder={t('recordForm.searchTitle')}
            autoFocus
            onChange={(e) => setTerm(e.target.value)}
          />
        </div>

        <div className="lop-search-modal__body">
          {showEmpty ? (
            <div className="lop-search-modal__empty">
              {t('recordForm.noResults')}
            </div>
          ) : (
            <div className="lop-search-modal__grid">
              <LopAgGrid<Row>
                rowData={rows}
                columnDefs={columnDefs}
                rowSelection={{ mode: 'singleRow' }}
                onSelectionChanged={onSelectionChanged}
                onRowDoubleClicked={onRowDoubleClicked}
              />
            </div>
          )}
          {loading && (
            <div className="lop-search-modal__loading">
              {t('recordForm.loading')}
            </div>
          )}
        </div>

        <footer className="lop-search-modal__footer">
          <button
            type="button"
            className="lop-search-modal__btn lop-search-modal__btn--primary"
            disabled={!selected}
            onClick={() => handleConfirm(selected)}
          >
            {t('recordForm.select')}
          </button>
          <button
            type="button"
            className="lop-search-modal__btn lop-search-modal__btn--secondary"
            onClick={onClose}
          >
            {t('recordForm.cancel')}
          </button>
        </footer>
      </div>
    </div>
  );
}
